import { useEffect, useMemo, useState } from 'react';
import {
  applyActionCode,
  checkActionCode,
  confirmPasswordReset,
  verifyPasswordResetCode,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { Link, useLocation } from 'react-router-dom';
import { auth, functions } from '../firebase-client';
import PasswordInput from '../components/forms/formComponents/PasswordInput';
import { passwordMeetsRequirements } from '../utils/passwordValidation';

const INVALID_ACTION_LINK_MESSAGE = 'This account link has expired or is no longer valid. Please request a new link.';
const INVALID_RESET_LINK_MESSAGE = 'This password reset link has expired or is no longer valid. Please request a new reset link.';
const syncMyAuthEmailFn = httpsCallable(functions, 'syncMyAuthEmail');

function normalizeOperation(value = '') {
  return String(value || '').toUpperCase().replace(/-/g, '_');
}

function resolveEmailActionType(mode = '', operation = '') {
  const normalizedMode = String(mode || '').trim();
  const normalizedOperation = normalizeOperation(operation);

  if (normalizedMode === 'verifyAndChangeEmail' || normalizedOperation === 'VERIFY_AND_CHANGE_EMAIL') {
    return 'verifyAndChangeEmail';
  }
  if (normalizedMode === 'recoverEmail' || normalizedOperation === 'RECOVER_EMAIL') {
    return 'recoverEmail';
  }
  if (normalizedMode === 'verifyEmail' || normalizedOperation === 'VERIFY_EMAIL') {
    return 'verifyEmail';
  }
  return '';
}

async function syncAuthEmailIfSignedIn() {
  if (!auth.currentUser) return null;
  try {
    return await syncMyAuthEmailFn();
  } catch (err) {
    return null;
  }
}

function ActionCard({ title = 'Account action', children }) {
  return (
    <div className="card" style={{ maxWidth: '520px', marginInline: 'auto' }}>
      <h1 className="section-title">{title}</h1>
      {children}
    </div>
  );
}

export default function AccountActionPage() {
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search || ''), [location.search]);
  const mode = params.get('mode') || '';
  const actionCode = params.get('oobCode') || '';
  const isResetAction = mode === 'resetPassword';

  const [status, setStatus] = useState(actionCode && mode ? 'checking' : 'invalid');
  const [message, setMessage] = useState('');
  const [error, setError] = useState(actionCode && mode ? '' : INVALID_ACTION_LINK_MESSAGE);
  const [verifiedEmail, setVerifiedEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetSaving, setResetSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;

    setMessage('');
    setError('');
    setResetError('');
    setVerifiedEmail('');
    setNewPassword('');
    setConfirmPassword('');

    if (!actionCode || !mode) {
      setStatus('invalid');
      setError(INVALID_ACTION_LINK_MESSAGE);
      return () => {
        cancelled = true;
      };
    }

    setStatus('checking');

    if (mode === 'resetPassword') {
      verifyPasswordResetCode(auth, actionCode)
        .then((accountEmail) => {
          if (cancelled) return;
          setVerifiedEmail(accountEmail || '');
          setStatus('resetReady');
        })
        .catch(() => {
          if (cancelled) return;
          setStatus('invalid');
          setError(INVALID_RESET_LINK_MESSAGE);
        });

      return () => {
        cancelled = true;
      };
    }

    checkActionCode(auth, actionCode)
      .then(async (info) => {
        if (cancelled) return;
        const actionType = resolveEmailActionType(mode, info?.operation);
        if (!actionType) {
          setStatus('unsupported');
          setError('This account link is not supported here.');
          return;
        }

        await applyActionCode(auth, actionCode);
        if (cancelled) return;

        if (typeof auth.currentUser?.reload === 'function' && actionType === 'verifyEmail') {
          try {
            await auth.currentUser.reload();
          } catch {
            // The action code has already been applied. A later auth refresh will
            // pick up the verified flag if this immediate refresh is unavailable.
          }
          if (cancelled) return;
        }

        if (actionType === 'verifyAndChangeEmail') {
          await syncAuthEmailIfSignedIn();
          setStatus('complete');
          setMessage('Your email address has been verified and updated.');
          return;
        }

        if (actionType === 'recoverEmail') {
          await syncAuthEmailIfSignedIn();
          setStatus('complete');
          setMessage('Your email address change has been undone.');
          return;
        }

        setStatus('complete');
        setMessage('Your email address has been verified.');
      })
      .catch(() => {
        if (cancelled) return;
        setStatus('invalid');
        setError(INVALID_ACTION_LINK_MESSAGE);
      });

    return () => {
      cancelled = true;
    };
  }, [actionCode, mode]);

  const handleConfirmReset = async (event) => {
    event.preventDefault();
    setResetError('');

    if (!passwordMeetsRequirements(newPassword)) {
      setResetError('Choose a password that meets the requirements below.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setResetError('Passwords do not match.');
      return;
    }

    setResetSaving(true);
    try {
      await confirmPasswordReset(auth, actionCode, newPassword);
      setStatus('complete');
      setMessage('Your password has been updated. You can now log in with your new password.');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      if (err?.code === 'auth/weak-password') {
        setResetError('Choose a stronger password that meets the requirements below.');
      } else {
        setResetError(INVALID_RESET_LINK_MESSAGE);
      }
    } finally {
      setResetSaving(false);
    }
  };

  const renderContent = () => {
    if (status === 'checking') {
      return <p style={{ marginBottom: 0 }}>Checking your account link...</p>;
    }

    if (status === 'resetReady') {
      return (
        <>
          <p className="section-text" style={{ marginBottom: '1.25rem' }}>
            Choose a new password for {verifiedEmail || 'your Member Nest account'}.
          </p>
          <form onSubmit={handleConfirmReset} className="flex-column" style={{ gap: '1rem' }} autoComplete="on">
            <PasswordInput
              inputId="account-action-new-password"
              label="New password"
              name="newPassword"
              autoComplete="new-password"
              placeholder="New password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              showRequirements
              confirmValue={confirmPassword}
              onConfirmChange={(event) => setConfirmPassword(event.target.value)}
              confirmInputId="account-action-confirm-new-password"
              confirmLabel="Confirm new password"
              confirmName="confirmNewPassword"
              confirmAutoComplete="new-password"
              confirmPlaceholder="Confirm new password"
              disabled={resetSaving}
            />
            <button type="submit" className="button" disabled={resetSaving}>
              {resetSaving ? 'Updating...' : 'Update password'}
            </button>
            {resetError && <p style={{ color: '#991b1b', margin: 0 }}>{resetError}</p>}
          </form>
        </>
      );
    }

    if (status === 'complete') {
      return (
        <>
          <p style={{ color: '#166534', marginTop: 0 }}>{message}</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
            <Link to="/profile" className="button">Go to profile</Link>
            <Link to="/login" className="button">Go to login</Link>
          </div>
        </>
      );
    }

    return (
      <>
        <p style={{ color: '#991b1b', marginTop: 0 }}>{error || INVALID_ACTION_LINK_MESSAGE}</p>
        <p style={{ marginBottom: 0 }}>
          {isResetAction ? (
            <Link to="/reset-password">Request a new reset link</Link>
          ) : (
            <Link to="/profile">Return to profile</Link>
          )}
        </p>
      </>
    );
  };

  return (
    <div className="page">
      <section className="section">
        <ActionCard title={isResetAction ? 'Reset your password' : 'Account confirmation'}>
          {renderContent()}
        </ActionCard>
      </section>
    </div>
  );
}

export {
  INVALID_ACTION_LINK_MESSAGE,
  INVALID_RESET_LINK_MESSAGE,
  resolveEmailActionType,
};
